import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { useAgentSelect } from "@/components/site/AgentSelect";
import { LS, KMD, CARRIERS } from "@/data";

const PARTNERS = [
  {
    id: "legalshield",
    logo: LS.logo,
    logoBg: "bg-white",
    name: "LegalShield",
    accent: "#b98bff",
    color: LS.purple,
    tagline: "Legal & identity protection for one flat monthly rate",
    body: "For over 50 years, LegalShield has made access to justice affordable. Through our partnership, families and small businesses get dedicated provider law firms, will preparation, document review, 24/7 emergency legal access, and IDShield identity protection — without hourly attorney fees.",
    points: ["Legal plans from $29.95/month", "IDShield identity protection from $14.95/month", "Dedicated provider law firms in every state", "Small business & commercial driver plans"],
    internal: "/legalshield",
    internalLabel: "Explore LegalShield Plans",
    external: LS.signup,
    externalLabel: "Become a Member",
  },
  {
    id: "konnectmd",
    logo: KMD.logo,
    logoBg: "bg-white",
    name: "KonnectMD",
    accent: "#6db6e8",
    color: KMD.blue,
    tagline: "24/7 virtual healthcare — no insurance required",
    body: "KonnectMD gives families and businesses affordable, unlimited access to board-certified physicians nationwide — with no visit fees, no copays, and over 1,000 common medications included free. Memberships cover your partner and children under 26 for one predictable monthly rate.",
    points: ["Memberships from $59.99/month", "1,000+ acute & chronic medications free", "Mental health, dermatology & specialist access", "Small business employee healthcare from ~$2/day"],
    internal: "/konnectmd",
    internalLabel: "Explore KonnectMD Plans",
    external: KMD.signup,
    externalLabel: "Enroll Today",
  },
  {
    id: "mightywell",
    logo: "/partners/mightywell-logo.webp",
    logoBg: "bg-white",
    logoSize: "h-12 md:h-14",
    name: "MightyWell Health",
    accent: "#34d399",
    color: "#10b981",
    tagline: "Everyday care meets major medical protection",
    body: "MightyWELL Health bundles everyday care — copays, prescriptions, labs, and 24/7 $0-copay virtual urgent care — with protection for major medical events through modern health sharing. Choose the Copay, HSA, or Concierge bundle and keep the doctors and hospitals you trust with a nationwide open network.",
    points: ["Copay, HSA & Concierge bundles", "24/7 virtual urgent care — $0 copay", "Nationwide open network & transparent pricing", "Living Benefit & Recuro mental health add-ons"],
    internal: "/healthcare",
    internalLabel: "Explore Health Bundles",
    external: null,
    externalLabel: null,
  },
  {
    id: "novae",
    logo: "/partners/novae-logo.jpg",
    logoBg: "bg-white",
    name: "Novae",
    accent: "#5fb7b9",
    color: "#2e8b8e",
    tagline: "The credit and money company",
    body: "Novae helps everyday families and entrepreneurs take control of their financial future — from credit education and credit building to business funding access and modern money tools. Through our partnership, Heritage clients get a trusted path to stronger credit and real financial power.",
    points: ["Credit education & credit-building tools", "Business funding access for entrepreneurs", "Modern money & payment solutions", "Financial literacy for the whole family"],
    internal: null,
    internalLabel: null,
    external: null,
    externalLabel: null,
  },
  {
    id: "oic",
    logo: "/partners/oic-logo.png",
    logoBg: "bg-white",
    logoSize: "h-20 md:h-28",
    name: "Our Infinite Company",
    accent: "#a06bc9",
    color: "#5b2d84",
    tagline: "Infinite possibilities. One mission.",
    body: "Our Infinite Company is a movement of entrepreneurs building generational wealth through financial education, leadership, and ownership. As part of the Infinite family, Heritage agents plug into world-class mentorship, a proven system, and a community that believes your family's future has no ceiling.",
    points: ["Entrepreneur-first mentorship & leadership development", "A proven system for building your own agency", "Financial education for families and business owners", "A culture of ownership and generational wealth"],
    internal: null,
    internalLabel: null,
    external: null,
    externalLabel: null,
  },
  {
    id: "wfg",
    logo: "/partners/wfg-logo.png",
    logoBg: "bg-white",
    name: "WFG — World Financial Group",
    accent: "#8fa3ad",
    color: "#47545c",
    tagline: "A platform trusted by thousands of financial professionals",
    body: "World Financial Group connects families with licensed financial professionals and industry-leading providers of life insurance, retirement, and wealth-building strategies. Through our WFG affiliation, Heritage delivers a full financial-needs analysis and access to solutions from some of the most respected carriers in North America.",
    points: ["Complimentary financial needs analysis", "Life insurance & indexed strategies from leading carriers", "Retirement income & wealth-building solutions", "A licensed-professional platform with real accountability"],
    internal: null,
    internalLabel: null,
    external: null,
    externalLabel: null,
  },
];

export default function PartnersPage() {
  const { openAgentSelect } = useAgentSelect();
  return (
    <>
      <PageHeader
        overline="Trusted Alliances"
        title="Our"
        accent="partners."
        subtitle="We hand-pick partners whose products protect families the way we would protect our own."
      />

      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <div
                data-testid={`partner-card-${p.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-3xl border border-white/10 bg-[#0a192f] p-8 md:p-12 transition-colors duration-500"
                style={{ borderColor: `${p.color}40` }}
              >
                <div className="lg:col-span-7">
                  <div className={`inline-flex items-center rounded-xl ${p.logoBg} px-5 py-3`}>
                    <img src={p.logo} alt={p.name} className={`${p.logoSize || "h-9 md:h-10"} w-auto object-contain`} />
                  </div>
                  <h2 className="mt-6 font-serif-display text-3xl md:text-4xl text-white">
                    {p.name} — <span className="italic" style={{ color: p.accent }}>{p.tagline}</span>
                  </h2>
                  <p className="mt-4 font-body font-light text-base md:text-lg leading-relaxed text-white/60">{p.body}</p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    {p.internal && (
                    <Link
                      to={p.internal}
                      data-testid={`partner-internal-${p.id}`}
                      className="btn-sweep inline-flex items-center gap-2 rounded-full px-7 py-3 font-body text-sm font-semibold tracking-wide text-white transition-transform duration-300 hover:scale-[1.03]"
                      style={{ backgroundColor: p.color }}
                    >
                      {p.internalLabel} <ArrowUpRight size={15} />
                    </Link>
                    )}
                    {p.id === "konnectmd" ? (
                      <button
                        onClick={openAgentSelect}
                        data-testid={`partner-external-${p.id}`}
                        className="inline-flex items-center gap-2 rounded-full border px-7 py-3 font-body text-sm tracking-wide text-white/80 hover:text-white transition-colors duration-300"
                        style={{ borderColor: `${p.color}70` }}
                      >
                        {p.externalLabel} <ArrowUpRight size={15} />
                      </button>
                    ) : (
                    p.external && (
                    <a
                      href={p.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`partner-external-${p.id}`}
                      className="inline-flex items-center gap-2 rounded-full border px-7 py-3 font-body text-sm tracking-wide text-white/80 hover:text-white transition-colors duration-300"
                      style={{ borderColor: `${p.color}70` }}
                    >
                      {p.externalLabel} <ArrowUpRight size={15} />
                    </a>
                    )
                    )}
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <ul className="space-y-3 rounded-2xl border border-white/10 bg-black/40 p-7 h-full">
                    <p className="font-body text-[11px] tracking-[0.25em] uppercase text-white/40 mb-4">Partnership Highlights</p>
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 font-body text-sm md:text-base text-white/70">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: p.accent }} />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.08}>
            <div className="pt-8">
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Life Insurance Carriers</p>
              <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
                The carriers behind <span className="text-gold-gradient italic">every quote.</span>
              </h2>
              <p className="mt-5 max-w-2xl font-body font-light text-base md:text-lg text-white/60">
                We shop your coverage across a network of respected national carriers — so your family
                gets the strongest option, not a single company's shelf.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CARRIERS.map((c, i) => (
              <Reveal key={c.name} delay={(i % 3) * 0.05}>
                <div
                  data-testid={`carrier-card-${c.domain.split(".")[0]}`}
                  className="h-full rounded-2xl border border-white/10 bg-[#0a192f] p-6 transition-colors duration-500 hover:border-[#bf953f]/45"
                >
                  <div className="flex items-center gap-4">
                    <span className={`inline-flex h-12 shrink-0 items-center justify-center rounded-xl bg-white p-2 ${c.logo ? "px-3" : "w-12"}`}>
                      <img
                        src={c.logo || `https://www.google.com/s2/favicons?domain=${c.domain}&sz=128`}
                        alt={`${c.name} logo`}
                        className={c.logo ? "h-8 w-auto max-w-[120px] object-contain" : "h-8 w-8 object-contain"}
                        loading="lazy"
                      />
                    </span>
                    <h3 className="font-serif-display text-xl md:text-2xl text-white">{c.name}</h3>
                  </div>
                  <p className="mt-4 font-body font-light text-sm leading-relaxed text-white/55">{c.blurb}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.09}>
            <div className="rounded-3xl border border-white/10 bg-[#0a192f] p-8 md:p-12" data-testid="partners-wireless-section">
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Wireless Partnership</p>
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5 flex flex-wrap items-center gap-5">
                  <div className="inline-flex items-center rounded-2xl bg-white px-6 py-4">
                    <img
                      src="https://customer-assets-jt897jd0.emergentagent.net/job_legacy-secure-12/artifacts/57o32f5f_NEW%20DM%20Logo.webp"
                      alt="DM Wireless Connect — Authorized AT&T Dealer"
                      data-testid="partners-dm-wireless-logo"
                      className="h-24 md:h-28 w-auto object-contain"
                    />
                  </div>
                  <div className="inline-flex items-center rounded-2xl bg-white overflow-hidden">
                    <img
                      src="/att/att-authorized-dealer.jpg"
                      alt="AT&T Authorized Dealer"
                      data-testid="partners-att-logo"
                      className="h-24 md:h-28 w-auto object-contain"
                    />
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                    DM Wireless Connect <span className="text-gold-gradient italic">and AT&amp;T.</span>
                  </h2>
                  <p className="mt-4 font-body font-light text-base md:text-lg text-white/60 leading-relaxed">
                    Through our partnership with DM Wireless Connect, an AT&amp;T authorized dealer, and AT&amp;T,
                    Heritage Family Protection Agency is now an AT&amp;T authorized retailer. Heritage families
                    can access trusted wireless service and connectivity solutions from one of America's leading
                    networks — we now have mobile and fiber home internet at a very good price.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-[#bf953f]/25 bg-gradient-to-br from-[#12284a] to-[#0a192f] p-10 md:p-14 text-center">
              <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                One conversation, <span className="text-gold-gradient italic">every carrier.</span>
              </h2>
              <p className="mt-4 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
                Tell us about your family once — we'll compare rates and underwriting across our
                entire carrier network and bring back the best fit.
              </p>
              <Link
                to="/life-insurance#quote"
                data-testid="partners-quote-btn"
                className="btn-sweep mt-8 inline-flex items-center rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-medium tracking-wide text-black"
              >
                Get a Free Quote
              </Link>
              <p className="mt-6 font-body text-[11px] text-white/30 max-w-2xl mx-auto leading-relaxed">
                All trademarks and logos are property of their respective owners. Carrier availability and products vary by state.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
